import React from 'react';
import { useTranslation } from 'react-i18next';
import { useBuilding } from '../context/buildingContext';
import { useUser } from '../context/userContext';
import { convertCelsiusToFahrenheit } from '../utils/helpers';
import { Building } from '../pages/BuildingSelector';
import { getBuildingBackground } from '../utils/imageHelpers';
import humidityIcon from '../assets/humidity_black.svg';
import wifiIcon from '../assets/wifi.svg';
import wifiIconDisconnected from '../assets/disconnected.svg';
import weatherSun from '../assets/weather/weather-sun.svg';
import weatherSunCloud from '../assets/weather/weather-sun-cloud.svg';
import weatherCloudyMoon from '../assets/weather/weather-cloudy-moon.svg';
import weatherMoon from '../assets/weather/weather-moon.svg';
import weatherRain from '../assets/weather/weather-rain.svg';
import weatherRainHeavy from '../assets/weather/weather-rain-heavy.svg';
import weatherSnow from '../assets/weather/weather-snow.svg';
import weatherWind from '../assets/weather/weather-wind.svg';
import { builders } from '../constants/builders';

interface DashboardHeaderProps {
  building: Building | null;
  outdoorData: {
    temperature: string;
    humidity: string;
  };
  connection: boolean;
  weatherCode: number | null;
}

const isNightTime = (): boolean => {
  const hour = new Date().getHours();
  return hour >= 20 || hour < 6;
};

const getWeatherIcon = (code: number | null): string => {
  const night = isNightTime();
  switch (code) {
    case 0: return night ? weatherMoon : weatherSun;
    case 1: return night ? weatherCloudyMoon : weatherSunCloud;
    case 2: return night ? weatherCloudyMoon : weatherSunCloud;
    case 3: return night ? weatherCloudyMoon : weatherRain;
    case 4: return night ? weatherCloudyMoon : weatherRainHeavy;
    case 5: return night ? weatherCloudyMoon : weatherSnow;
    case 6: return night ? weatherCloudyMoon : weatherWind;
    default: return night ? weatherMoon : weatherSun;
  }
};

const getWeatherLabelKey = (code: number | null): string => {
  const night = isNightTime();
  switch (code) {
    case 0: return night ? 'weather.clearNight' : 'weather.sunny';
    case 1: return 'weather.partlyCloudy';
    case 2: return 'weather.cloudy';
    case 3: return 'weather.rainy';
    case 4: return 'weather.heavyRain';
    case 5: return 'weather.snow';
    case 6: return 'weather.windy';
    default: return night ? 'weather.clearNight' : 'weather.sunny';
  }
};

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  building,
  outdoorData,
  connection,
  weatherCode
}) => {
  const { t } = useTranslation();
  const { unit } = useUser();
  const { buildingId } = useBuilding();
  const numericBuildingId = buildingId ? Number(buildingId) : 0;
  const builderData = builders[numericBuildingId as keyof typeof builders];
  const headerBg = getBuildingBackground(numericBuildingId) || '/background.webp';

  const displayTemperature = unit === 'Fahrenheit'
    ? convertCelsiusToFahrenheit(parseFloat(outdoorData.temperature) || 0)
    : outdoorData.temperature;
  const unitSymbol = unit === 'Fahrenheit' ? '°F' : '°C';

  return (
    <div className="dashboardHeader">
      <div className="headerBackground">
        {headerBg && <img src={headerBg}/>}
      </div>

      <div className="headerCard">
        <div className="cardContent">
          <div className='leftSection'>
            {/* Address Section */}
            <div className="addressSection">
              <div className="streetAddress">
                {building?.street || 'N/A'}
              </div>
              <div className="cityAddress">
                {building?.city && building?.postalCode
                  ? `${building.city}, ${building.postalCode}`
                  : 'N/A'
                }
              </div>
            </div>
            {/* Connection Status */}
            <div className="connectionSection">
              <div className={`connectionStatus ${connection ? 'connected' : 'disconnected'}`}>
                <div className="wifiIcon">
                  <img src={connection ? wifiIcon : wifiIconDisconnected} alt="wifi" />
                </div>
                <span className="connectionText">
                  {connection ? t('connected') : t('disconnected')}
                </span>
              </div>
            </div>
            {/* Company Logo */}
            {builderData && builderData.link !== null && (
              <a target="_blank" href={builderData?.link} className="companySection">
                <div className="companyLogo">
                  <img src={builderData?.builderLogo} alt="builder" />
                </div>
              </a>
            )}
          </div>

          <div className='rightSection'>
            {weatherCode !== null && (
              <div className="weatherIcon">
                <img src={getWeatherIcon(weatherCode)} alt="weather" />
                <span className="weatherLabel">{t(getWeatherLabelKey(weatherCode))}</span>
              </div>
            )}
            <div className="weatherInfo">
              <div className="temperature">{displayTemperature}{unitSymbol}</div>
              <div className="humidity">
                <img src={humidityIcon} alt="humidity" />
                <span>{outdoorData.humidity}%</span>
              </div>
            </div>
          </div>
        </div>
        {
          building?.disabled && (
            <div className="buildingChildLock">
              <span>{t('buildingDisabled')}</span>
            </div>
          )
        }
        {
          building?.childLock && (
            <div className="buildingChildLock">
              <span>{t('hasBuildingChildLock')} {building?.childLockTemperatureDegC} °C</span>
            </div>
          )
        }
      </div>
    </div>
  );
};

export default DashboardHeader;
